/**
 * readBrowserLocalLane — reads browser-local preview state from localStorage.
 *
 * Keys defined in: 07-preview-storage-and-feedback-contract.md
 * - kea_storefront_preview_mode_v1     (string: 'enabled'/'disabled')
 * - kea_storefront_preview_feedback_v1 (JSON blob)
 *
 * This reader never throws. If localStorage is unavailable or data is
 * corrupt, it returns a degraded result.
 *
 * @param {Storage} [storage=window.localStorage] - Storage interface for testability
 * @returns {object} Normalized browser-local lane result
 */

import { PREVIEW_FEEDBACK_STALE_MS } from '../operatorState';

// ---------------------------------------------------------------------------
// Key constants (doc 07 §3)
// ---------------------------------------------------------------------------

export const PREVIEW_MODE_KEY = 'kea_storefront_preview_mode_v1';
export const PREVIEW_FEEDBACK_KEY = 'kea_storefront_preview_feedback_v1';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function safeGetItem(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function parsePreviewFeedback(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.version) return null;
    return parsed;
  } catch {
    return null;
  }
}

function isFeedbackStale(feedback, now) {
  if (!feedback || !feedback.updated_at) return true;
  const updatedAt = new Date(feedback.updated_at).getTime();
  if (Number.isNaN(updatedAt)) return true;
  return (now - updatedAt) > PREVIEW_FEEDBACK_STALE_MS;
}

// ---------------------------------------------------------------------------
// Main reader
// ---------------------------------------------------------------------------

export function readBrowserLocalLane(storage, now) {
  // Default to window.localStorage — accept param for testability.
  // Explicit null means "no storage available"; only undefined triggers fallback.
  const store = storage !== undefined
    ? storage
    : (typeof window !== 'undefined' ? window.localStorage : null);
  const timestamp = now || Date.now();

  if (!store) {
    return {
      available: false,
      previewMode: null,
      previewFeedback: null,
      feedbackStale: true,
    };
  }

  const rawMode = safeGetItem(store, PREVIEW_MODE_KEY);
  const previewMode = (rawMode === 'enabled' || rawMode === 'disabled') ? rawMode : null;

  const rawFeedback = safeGetItem(store, PREVIEW_FEEDBACK_KEY);
  const previewFeedback = parsePreviewFeedback(rawFeedback);
  const feedbackStale = previewFeedback ? isFeedbackStale(previewFeedback, timestamp) : true;

  return {
    available: true,
    previewMode,
    previewFeedback,
    feedbackStale,
  };
}
